import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// =============================================================================
// ROYAL BRIDGE - Agent Engagement API
// =============================================================================

// V2 migration note: Royal Bridge is on the `*_V2` request types but sends NO agency key at all
// (no `agency_id`, no `travel_agency_uid`) — every Agent Engagement endpoint is HQ-aggregate.
// See V2_API_MIGRATION.md.

// Helper to build common request data (for most APIs) — no agency key
const buildRequestData = (params) => ({
    user_uid: getUserDetails()?.user_details?.uid,
    country: params.country || "",
    type: params.type,
    start_date: params.startDate,
    end_date: params.endDate || null,
});

// Helper for "by month" APIs that only need year parameter — no agency key
const buildYearRequestData = (params) => {
    // Extract year from startDate which can be in different formats:
    // - "2025" (year filter)
    // - "2025-02" (month filter)
    // - "2025-01-01" (date_range filter)
    const startDate = params.startDate || "";
    const year = startDate.split("-")[0] || new Date().getFullYear().toString();
    return {
        user_uid: getUserDetails()?.user_details?.uid,
        country: params.country || "",
        year,
    };
};

// Club Learn helpers — kept as thin pass-throughs (no agency key on V2)
const buildClubLearnRequestData = (params) => buildRequestData(params);

const buildClubLearnYearRequestData = (params) => buildYearRequestData(params);

export const royalBridgeAgentEngagementApi = createApi({
    reducerPath: "royalBridgeAgentEngagementApi",
    baseQuery: apiRequestBaseQuery,
    refetchOnMountOrArgChange: true,
    tagTypes: ["RoyalGenie", "ClubLearn", "UpperDeck"],
    endpoints: (builder) => ({
        // =====================================================================
        // ROYAL GENIE
        // =====================================================================

        getTotalAgentsSignedUp: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalAgentsSignedUpV2,
                data: buildRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Agents Signed Up By Month (uses year-only format)
        getAgentsSignedUpByMonth: builder.query({
            query: (params) => ({
                requestType: requestType.getAgentsSignedUpByMonthV2,
                data: buildYearRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Proposals Generated By Month (uses year-only format)
        getProposalsGeneratedByMonth: builder.query({
            query: (params) => ({
                requestType: requestType.getProposalsGeneratedByMonthV2,
                data: buildYearRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Total Proposals Generated
        getTotalProposalsGenerated: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalProposalsGeneratedV2,
                data: buildRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Product Mix Genie Proposals
        getProductMixGenieProposals: builder.query({
            query: (params) => ({
                requestType: requestType.getProductMixGenieProposalsV2,
                data: buildRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Top 10 Itineraries Genie Proposals
        getTop10ItinerariesGenieProposals: builder.query({
            query: (params) => ({
                requestType: requestType.getTop10ItinerariesGenieProposalsV2,
                data: buildRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        getStoreBreakdown: builder.query({
            query: (params) => ({
                requestType: requestType.getStoreBreakdownV2,
                data: buildRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // =====================================================================
        // CLUB LEARN
        // =====================================================================

        getFocusModules: builder.query({
            query: (params) => ({
                requestType: requestType.getFocusModulesV2,
                data: buildClubLearnYearRequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getMyRoyalGuruTotals: builder.query({
            query: (params) => ({
                requestType: requestType.getMyRoyalGuruTotalsV2,
                data: buildClubLearnRequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getStoresWithRoyalGuru: builder.query({
            query: (params) => ({
                requestType: requestType.getStoresWithRoyalGuruV2,
                data: buildClubLearnRequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getStoresWithoutRoyalGuru: builder.query({
            query: (params) => ({
                requestType: requestType.getStoresWithoutRoyalGuruV2,
                data: buildClubLearnRequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getTotalModulesCompleted: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalModulesCompletedV2,
                data: buildClubLearnRequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getClubLearnModulesCompletedByMonth: builder.query({
            query: (params) => ({
                requestType: requestType.getClubLearnModulesCompletedByMonthV2,
                data: buildClubLearnYearRequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getTopStoreGuru: builder.query({
            query: (params) => ({
                requestType: requestType.getTopStoreGuruV2,
                data: { ...buildClubLearnRequestData(params), limit: params.limit || 100 },
            }),
            providesTags: ["ClubLearn"],
        }),

        // =====================================================================
        // UPPER DECK
        // Note: Uses same getTotalAgentsSignedUp & getAgentsSignedUpByMonth from Royal Genie
        // =====================================================================

        getTotalBookingsRedeemed: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalBookingsRedeemedV2,
                data: buildRequestData(params),
            }),
            providesTags: ["UpperDeck"],
        }),

        getBookingsRedeemedByMonth: builder.query({
            query: (params) => ({
                requestType: requestType.getBookingsRedeemedByMonthV2,
                data: buildYearRequestData(params),
            }),
            providesTags: ["UpperDeck"],
        }),
    }),
});

// Export hooks for usage in components
export const {
    // Royal Genie
    useGetTotalAgentsSignedUpQuery,
    useGetAgentsSignedUpByMonthQuery,
    useGetProposalsGeneratedByMonthQuery,
    useGetTotalProposalsGeneratedQuery,
    useGetProductMixGenieProposalsQuery,
    useGetTop10ItinerariesGenieProposalsQuery,
    useGetStoreBreakdownQuery,
    // Club Learn
    useGetFocusModulesQuery,
    useGetMyRoyalGuruTotalsQuery,
    useGetStoresWithRoyalGuruQuery,
    useGetStoresWithoutRoyalGuruQuery,
    useGetTotalModulesCompletedQuery,
    useGetClubLearnModulesCompletedByMonthQuery,
    useGetTopStoreGuruQuery,
    // Upper Deck
    useGetTotalBookingsRedeemedQuery,
    useGetBookingsRedeemedByMonthQuery,
} = royalBridgeAgentEngagementApi;
